// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useLocation, useMatches, useParams } from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SyncDisabledRoundedIcon from '@mui/icons-material/SyncDisabledRounded';
import SyncProblemRoundedIcon from '@mui/icons-material/SyncProblemRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';

// material ui
import {
  AppBar,
  Badge,
  Box,
  Button,
  Card,
  ClickAwayListener,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Popper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { WritableOnly } from '@/components/access-control';
import { ServiceGuard } from '@/components/access-control/ServiceGuard';
import PageContainer from '@/components/layout/PageContainer';
import StyledCustomLink from '@/components/link/StyledCustomLink';
import { CopyToClipboardButton } from '@/components/ui/button/CopyToClipboardButton';

// feature-components
import DateRangeSelector from '@/features/energyxplorer/components/daterange/DateRangeSelector';
import EsgDateRangeSelector from '@/features/energyxplorer/components/daterange/EsgDateRangeSelector';
import DeleteEnergyGroupDialog from '@/features/energyxplorer/components/energygroup/DeleteEnergyGroupDialog';

// feature-hooks
import useEnergyAnalysisMenuItems from '@/features/energyxplorer/hooks/useEnergyAnalysisMenuItems';

// feature-utils
import {
  showDateRangeSelector,
  showEnergyGroupSettings,
} from '@/features/energyxplorer/utils/energyAnalysisMenuHelpers';

// components
// eslint-disable-next-line import/no-restricted-paths
import DaluxTicketDialog from '@/features/dalux-integration/DaluxTicketDialog';

import { keyframes } from '@emotion/react';

const StatusChip = ({ status }) => {
  const { t } = useTranslation('energyxplorer');
  const label = t(`app_bar.status.${status}`);
  switch (status) {
    case 'Synced':
      return (
        <Tooltip title={label} placement="top">
          <IconButton
            disableRipple
            sx={{ cursor: 'default' }}
            data-testid="energy-analysis-status-chip"
            data-status={status}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <CheckCircleRoundedIcon
                  sx={{
                    fontSize: 14,
                    color: 'success.main',
                    bgcolor: 'background.paper',
                    borderRadius: '50%',
                  }}
                />
              }
            >
              <SyncRoundedIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      );
    case 'Desynced':
      return (
        <Tooltip title={label} placement="top">
          <IconButton
            disableRipple
            sx={{ cursor: 'default' }}
            data-testid="energy-analysis-status-chip"
            data-status={status}
          >
            <SyncProblemRoundedIcon />
          </IconButton>
        </Tooltip>
      );
    case 'Processing':
      return (
        <Tooltip title={label} placement="top">
          <IconButton
            disableRipple
            sx={{ cursor: 'default' }}
            data-testid="energy-analysis-status-chip"
            data-status={status}
          >
            <SyncRoundedIcon
              color="info"
              sx={{
                animation: 'spinReverse 2s linear infinite',
                '@keyframes spinReverse': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(-360deg)' }, // counter-clockwise
                },
              }}
            />
          </IconButton>
        </Tooltip>
      );
    default:
      return (
        <Tooltip title={label} placement="top">
          <IconButton
            disableRipple
            sx={{ cursor: 'default' }}
            data-testid="energy-analysis-status-chip"
            data-status={status}
          >
            <SyncDisabledRoundedIcon />
          </IconButton>
        </Tooltip>
      );
  }
};

// Define the animation
const pulseBorder = keyframes`
  0% { box-shadow: 0 0 0 0 #4CAF50; }
  70% { box-shadow: 0 0 0 6px rgba(0, 255, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0); }
`;
function useHoverPopper() {
  const [openKey, setOpenKey] = React.useState(null); // e.g. "app-0" | "settings" | null
  const [anchorEl, setAnchorEl] = React.useState(null);

  const closeTimerRef = React.useRef(null);

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = React.useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpenKey(null);
      setAnchorEl(null);
    }, 120);
  }, [clearCloseTimer]);

  const open = React.useCallback(
    (key, el) => {
      clearCloseTimer();
      setOpenKey(key);
      setAnchorEl(el);
    },
    [clearCloseTimer],
  );

  const closeNow = React.useCallback(() => {
    clearCloseTimer();
    setOpenKey(null);
    setAnchorEl(null);
  }, [clearCloseTimer]);

  React.useEffect(() => {
    return () => clearCloseTimer();
  }, [clearCloseTimer]);

  return {
    openKey,
    anchorEl,
    open,
    closeNow,
    scheduleClose,
    clearCloseTimer,
  };
}

function EnergyAnalysisAppBar() {
  const { t } = useTranslation('energyxplorer', 'common');
  const location = useLocation();
  const matches = useMatches();

  const { energyGroupId } = useParams({ strict: false });

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );

  const [daluxTicketOpen, setDaluxTicketOpen] = React.useState(false);

  const EsgRendered = React.useMemo(
    () => location.pathname.includes('/esg'),
    [location.pathname],
  );

  const [deleteEnergyGroupDialogOpen, setDeleteEnergyGroupDialogOpen] =
    React.useState(false);

  const { appBarItems } = useEnergyAnalysisMenuItems();

  // Popper controller for the nav item submenus.
  const popper = useHoverPopper();

  // Dedicated anchor/open state for the settings popper, kept separate from
  // the nav submenu popper above so its anchorEl is always the settings
  // icon button itself (never swapped out by hovering an unrelated item).
  const [settingsAnchorEl, setSettingsAnchorEl] = React.useState(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  // Nearest match wins: walk the route trail from the current (most
  // specific) match up towards the root, and hide/show the app bar based on
  // the first `staticData.hideEnergyAnalysisAppBar` a route in that trail
  // defines. Lets a descendant route (e.g. portfolio/$analysisType) re-show
  // the app bar even though an ancestor (portfolio) hides it.
  const hideAppBarMatch = [...matches]
    .reverse()
    .find((match) => match.staticData.hideEnergyAnalysisAppBar !== undefined);
  if (hideAppBarMatch?.staticData.hideEnergyAnalysisAppBar) {
    return null;
  }

  return (
    <PageContainer sx={{ p: 0 }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          borderRadius: 0,
          height: 64,
        }}
      >
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {appBarItems.map((item, index) => {
              const hasMenu = Boolean(item?.submenu?.length);
              const key = `app-${index}`;
              const isDisabled = item.disabled;
              const open = popper.openKey === key;
              const pathsToMatch = item.matchingPaths || [item.path];
              const isSelected =
                item.path &&
                (pathsToMatch.some((path) =>
                  location.pathname.includes(path),
                ) ||
                  item.submenu?.some((sub) =>
                    location.pathname.includes(sub.path),
                  ));

              let label = item.label;
              if (hasMenu && isSelected) {
                item.submenu.forEach((subItem) => {
                  if (
                    subItem.path &&
                    location.pathname.includes(subItem.path)
                  ) {
                    label = subItem.label;
                  }
                });
              }

              return (
                <Box
                  key={item.label}
                  sx={{ position: 'relative' }}
                  onMouseEnter={(e) => {
                    if (hasMenu && !isDisabled)
                      popper.open(key, e.currentTarget);
                  }}
                  onMouseLeave={() => {
                    if (hasMenu && !isDisabled) popper.scheduleClose();
                  }}
                >
                  <Button
                    disabled={isDisabled}
                    onMouseEnter={(e) => {
                      if (hasMenu) popper.open(key, e.currentTarget);
                    }}
                    onClick={() => {
                      item.onClick && !isDisabled && item.onClick();
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { md: 0, lg: 1 },
                      px: 1.5,
                      py: 1,
                      minWidth: 'unset',
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      borderRadius: 1,
                      color: 'text.primary',
                      bgcolor: isSelected ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: isSelected
                          ? 'action.selected'
                          : 'action.hover',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 0,
                        color: isSelected ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box
                      sx={{
                        display: { md: 'none', xl: 'flex' },
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <span>{label}</span>
                      {hasMenu && (
                        <KeyboardArrowRightRoundedIcon
                          fontSize="small"
                          sx={{
                            transition: 'transform 0.2s ease-in-out',
                            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                          }}
                        />
                      )}
                    </Box>
                  </Button>

                  {hasMenu && (
                    <Popper
                      open={open}
                      anchorEl={open ? popper.anchorEl : null}
                      placement="bottom-start"
                      transition
                      disablePortal={false}
                      strategy="fixed"
                      elevation={4}
                      sx={{ zIndex: 1301 }}
                    >
                      {({ TransitionProps }) => (
                        <Grow
                          {...TransitionProps}
                          style={{ transformOrigin: 'left top' }}
                        >
                          <Card
                            variant="outlined"
                            onMouseEnter={popper.clearCloseTimer}
                            onMouseLeave={popper.scheduleClose}
                          >
                            <ClickAwayListener onClickAway={popper.closeNow}>
                              <MenuList sx={{ minWidth: 220, p: 0.5 }}>
                                {item?.submenu?.map((subItem, subIndex) => (
                                  <MenuItem
                                    key={`${item.label}-${subIndex}`}
                                    onClick={() => {
                                      popper.closeNow();
                                      subItem.onClick && subItem.onClick();
                                    }}
                                    sx={{ borderRadius: 1 }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                    >
                                      {subItem.label}
                                    </Typography>
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </ClickAwayListener>
                          </Card>
                        </Grow>
                      )}
                    </Popper>
                  )}
                </Box>
              );
            })}
          </Stack>

          <Box display="flex" alignItems="center" gap={1}>
            <CopyToClipboardButton />
            {selectedEnergyGroup && (
              <ServiceGuard
                serviceNames={['DaluxIntegration']}
                fallback={<Box></Box>}
              >
                <Tooltip
                  title={t(
                    'dalux-integration:ticket.button',
                    'Opret Dalux Ticket',
                  )}
                >
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setDaluxTicketOpen(true)}
                  >
                    <ConfirmationNumberIcon />
                  </IconButton>
                </Tooltip>
              </ServiceGuard>
            )}
            {showEnergyGroupSettings(location.pathname) && (
              <StatusChip
                status={selectedEnergyGroup?.options?.state || 'Unknown'}
              />
            )}

            {showEnergyGroupSettings(location.pathname) && (
              <Box
                sx={{ display: 'flex', alignItems: 'center' }}
                onMouseEnter={() => setSettingsOpen(true)}
                onMouseLeave={() => setSettingsOpen(false)}
              >
                <IconButton
                  ref={setSettingsAnchorEl}
                  onClick={() => setSettingsOpen((prev) => !prev)}
                  sx={{
                    textTransform: 'none',
                    py: 1,
                    borderRadius: 2,
                    color: 'text.secondary',
                    animation: !selectedEnergyGroup
                      ? `${pulseBorder} 2s infinite`
                      : 'none',
                    boxShadow: !selectedEnergyGroup
                      ? '0 0 0 0 #4CAF50'
                      : 'none',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary',
                    },
                  }}
                >
                  <SettingsRoundedIcon />
                </IconButton>

                <Popper
                  open={settingsOpen}
                  anchorEl={settingsAnchorEl}
                  placement="bottom-end"
                  transition
                  disablePortal={false}
                  strategy="fixed"
                  elevation={4}
                  sx={{ zIndex: 1301 }}
                >
                  {({ TransitionProps }) => (
                    <Grow
                      {...TransitionProps}
                      style={{ transformOrigin: 'right top' }}
                    >
                      <Card variant="outlined">
                        <ClickAwayListener
                          onClickAway={() => setSettingsOpen(false)}
                        >
                          <WritableOnly>
                            <MenuList dense sx={{ minWidth: 240, p: 0.5 }}>
                              <MenuItem
                                component={StyledCustomLink}
                                to={`/$accountId/energyxplorer/deepdive/create`}
                                onClick={() => setSettingsOpen(false)}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <AddRoundedIcon />
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="inherit"
                                  >
                                    {t(
                                      'energy_group_settings.add_energy_group',
                                    )}
                                  </Typography>
                                </Box>
                              </MenuItem>
                              <MenuItem
                                component={StyledCustomLink}
                                to={`/$accountId/energyxplorer/deepdive/$energyGroupId/edit`}
                                disabled={!selectedEnergyGroup}
                                onClick={() => setSettingsOpen(false)}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <EditRoundedIcon />
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="inherit"
                                  >
                                    {t(
                                      'energy_group_settings.edit_energy_group',
                                    )}{' '}
                                    {selectedEnergyGroup?.name || ''}
                                  </Typography>
                                </Box>
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  setSettingsOpen(false);
                                  if (!selectedEnergyGroup) return;
                                  setDeleteEnergyGroupDialogOpen(true);
                                }}
                                disabled={!selectedEnergyGroup}
                                sx={{
                                  '&:hover': { color: 'error.main' },
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <DeleteRoundedIcon />
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {t(
                                      'energy_group_settings.delete_energy_group',
                                    )}{' '}
                                    {selectedEnergyGroup?.name || ''}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            </MenuList>
                          </WritableOnly>
                        </ClickAwayListener>
                      </Card>
                    </Grow>
                  )}
                </Popper>
              </Box>
            )}
            {!EsgRendered && showDateRangeSelector(location.pathname) && (
              <DateRangeSelector />
            )}
            {EsgRendered && showDateRangeSelector(location.pathname) && (
              <EsgDateRangeSelector />
            )}
          </Box>
        </Toolbar>

        {selectedEnergyGroup && (
          <>
            <DeleteEnergyGroupDialog
              open={deleteEnergyGroupDialogOpen}
              onClose={() => setDeleteEnergyGroupDialogOpen(false)}
            />
          </>
        )}
        {selectedEnergyGroup && (
          <DaluxTicketDialog
            open={daluxTicketOpen}
            onClose={() => setDaluxTicketOpen(false)}
            energyGroupName={selectedEnergyGroup?.name}
            locationId={selectedEnergyGroup?.locationId}
          />
        )}
      </AppBar>
    </PageContainer>
  );
}

export default EnergyAnalysisAppBar;
